import React, {ReactNode} from "react";
import {Changelog, EvalData, FilterDef, Question} from "../model";
import {TableView} from "./table";
import {Tooltip} from "react-tooltip";
import {copyTextToClipboard} from "../clipboard";

interface AppViewState {
    data: EvalData | null;
    metric: string | null;
    filters: { [key: string]: string };
    filterSet: Set<string>;
    modelTypeFilter: string;
    dateRangeFilter: string;
    providerFilter: string;
    sizeLimitFilter: string;
    examplesCategory: string;
    changelog: Changelog[];
}

interface QuickFilterOption {
    tag: string;
    name: string;
}

export class AppView extends React.Component<any, AppViewState> {
    constructor(props: any) {
        super(props);
        this.state = {
            data: null,
            metric: null,
            filters: {},
            filterSet: new Set(),
            modelTypeFilter: "all",
            dateRangeFilter: "all",
            providerFilter: "all",
            sizeLimitFilter: "all",
            examplesCategory: "culture & tradition",
            changelog: []
        };
    }

    componentDidMount() {
        let url = `${window.location.protocol}//${window.location.host}`;
        url += "/data.json";
        fetch(url)
            .then(val => val.json())
            .then(val => this.setState({data: val, filterSet: this.filters(val), changelog: this.changelog(val)}));
    }

    private filters(data: EvalData) {
        const filters = data?.filters || [];
        const filterSet = new Set<string>();
        for (const filter of filters) {
            const filterOptions = filter.options;
            if (filterOptions.length > 0) {
                filterSet.add(filterOptions[0].tag)
            }
        }
        return filterSet;
    }

    private changelog(data: EvalData) {
        let modelNames: any = {};
        for (const model of (data?.models || [])) {
            modelNames[model.id] = model.name;
        }
        const changelog: Changelog[] = (data?.results || []).map(val => {
            const updated = val.timestamp.substring(0, val.timestamp.indexOf(","));
            const parts = updated.split("/");
            const timestamp = new Date(parseInt(parts[2], 10), parseInt(parts[1], 10) - 1, parseInt(parts[0], 10));
            return {
                model: modelNames[val.id] || val.id,
                updated: updated,
                timestamp: timestamp
            };
        });
        changelog.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
        return changelog;
    }

    private renderFilters(): ReactNode[] {
        const filters = this.state.data?.filters || [];
        return filters.map(val => this.renderFiltersBlock(val));
    }

    private renderQuickFilters(): ReactNode {
        return (
            <div className="quick-filters">
                {this.renderQuickFilterBlock(
                    "Model type",
                    "model-type-filter",
                    this.state.modelTypeFilter,
                    [
                        {tag: "all", name: "ALL"},
                        {tag: "closed", name: "closed-weights"},
                        {tag: "open", name: "open-weights"}
                    ],
                    value => this.setState({...this.state, modelTypeFilter: value})
                )}
                {this.renderQuickFilterBlock(
                    "Date added",
                    "date-range-filter",
                    this.state.dateRangeFilter,
                    [
                        {tag: "all", name: "ALL"},
                        {tag: "ytd", name: "year to date"},
                        {tag: "1m", name: "1 month"},
                        {tag: "6m", name: "6 months"},
                        {tag: "1y", name: "1 year"}
                    ],
                    value => this.setState({...this.state, dateRangeFilter: value})
                )}
                {this.renderProviderFilterBlock()}
                {this.renderQuickFilterBlock(
                    "Size limit",
                    "size-limit-filter",
                    this.state.sizeLimitFilter,
                    [
                        {tag: "all", name: "ALL"},
                        {tag: "500B", name: "500B"},
                        {tag: "100B", name: "100B"},
                        {tag: "50B", name: "50B"},
                        {tag: "30B", name: "30B"},
                        {tag: "20B", name: "20B"},
                        {tag: "15B", name: "15B"},
                        {tag: "10B", name: "10B"},
                        {tag: "5B", name: "5B"}
                    ],
                    value => this.setState({...this.state, sizeLimitFilter: value})
                )}
            </div>
        );
    }

    private renderProviderFilterBlock(): ReactNode {
        const providers = this.providers();
        return (
            <div className="quick-filter-block" key="provider-filter">
                <strong>Provider</strong>
                <select className="quick-filter-select" value={this.state.providerFilter}
                        onChange={event => this.setState({...this.state, providerFilter: event.target.value})}>
                    <option value="all">ALL</option>
                    {providers.map(provider => <option value={provider} key={provider}>{provider}</option>)}
                </select>
            </div>
        );
    }

    private providers(): string[] {
        const resultIds = new Set((this.state.data?.results || []).map(result => result.id));
        const providers = new Set<string>();
        for (const model of (this.state.data?.models || [])) {
            const provider = model.columns?.["Provider"];
            if (resultIds.has(model.id) && typeof provider === "string" && provider.length > 0) {
                providers.add(provider);
            }
        }
        return Array.from(providers).sort((a, b) => a.localeCompare(b));
    }

    private renderQuickFilterBlock(
        label: string,
        name: string,
        selected: string,
        options: QuickFilterOption[],
        onSelect: (value: string) => void
    ): ReactNode {
        return (
            <div className="quick-filter-block" key={name}>
                <strong>{label}</strong>
                <select className="quick-filter-select" value={selected}
                        onChange={event => onSelect(event.target.value)}>
                    {options.map(opt => <option value={opt.tag} key={opt.tag}>{opt.name}</option>)}
                </select>
            </div>
        );
    }

    private renderFiltersBlock(spec: FilterDef): ReactNode {
        const filterOptions = spec.options;
        const selected = this.state.filters[spec.name] || filterOptions[0].tag;
        const options = [];
        for (const opt of filterOptions) {
            options.push(
                <input type="radio" id={opt.tag} name={spec.name} key={opt.tag}
                       value={opt.tag} checked={opt.tag === selected}
                       onChange={(event) => this.selectFilter(spec.name, event)}>
                </input>
            );
            options.push(<label htmlFor={opt.tag} key={"label " + opt.tag}>{opt.name || opt.tag}</label>)
        }
        return <div className="filter-block" key={spec.name}><strong>{spec.name}:</strong>{options}</div>;
    }

    private selectMetric(event: any) {
        this.setState({...this.state, metric: event.target.value});
    }

    private selectFilter(filter: string, event: any) {
        let filters = {...this.state.filters};
        filters[filter] = event.target.value === "none" ? null : event.target.value || null;
        const filterSet = new Set<string>(Object.values(filters).filter(val => val != null));
        this.setState({...this.state, filters: filters, filterSet: filterSet});
    }

    renderPaperInfo() {
        let el = [];
        if (this.state.data?.paper) {
            el.push(<a key="paper" href={this.state.data.paper} target="_blank">🗋 Paper</a>);
        }
        if (this.state.data?.citation) {
            el.push(
                <a key="cite" onClick={() => copyTextToClipboard(this.state.data?.citation)}
                   data-tooltip-id="table-tooltip" data-tooltip-content={"Citation copied to clipboard!"}>
                    🗍 Cite
                </a>
            );
        }
        return el.length == 0 ? null : <span className={"paper-info"}>{el}</span>;
    }

    renderExamples() {
        const questions = (this.state.data?.questions || [])
            .filter(val => val.category === this.state.examplesCategory);
        const rows = questions.map(val => this.renderExample(val));
        return (
            <div>
                {this.renderExamplesTabs()}
                <table className="q-table">
                    <thead>
                    <tr>
                        <th style={{width: "20px"}}>ID</th>
                        <th style={{width: "500px"}}>Question</th>
                        <th>Verification</th>
                        <th>Subcategory</th>
                    </tr>
                    </thead>
                    <tbody>{rows}</tbody>
                </table>
            </div>
        )
    }

    renderExamplesTabs() {
        const cat = ["culture & tradition", "art & entertainment", "geography", "history", "grammar", "vocab"]
        const options = [];
        const selected = this.state.examplesCategory;
        for (const category of cat) {
            options.push(
                <input type="radio" id={category} name={category} key={category}
                       value={category} checked={category === selected}
                       onChange={(event) => this.selectCategory(category)}>
                </input>
            );
            options.push(<label htmlFor={category} key={"label " + category}>{category}</label>);
        }
        return <div className="category-tabs">{options}</div>
    }

    renderExample(val: Question) {
        const conditions = [];
        for (const key of Object.keys(val.expect)) {
            let conditionValue;
            if (key === "regex") {
                conditionValue = val.expect[key].toString().replaceAll(",", ", ");
            } else {
                const items = [];
                for (const item of val.expect[key]) {
                    if (Array.isArray(item)) {
                        items.push(`{${item.toString()}}`)
                    } else {
                        items.push(item.toString());
                    }
                }
                conditionValue = items.join(",").replaceAll(",", ", ");
            }
            conditions.push(`<strong>${key}</strong>: ${conditionValue}\n`);
        }
        if (val.params) {
            const params = [];
            for (const [param_name, param_val] of Object.entries(val.params)) {
                params.push(`${param_name}=${param_val}`)
            }
            conditions.push("<strong>params:</strong> " + params.join(", "));
        }
        return (
            <tr key={`example_${val.id}`}>
                <td>{val.id}</td>
                <td>{val.question}</td>
                <td dangerouslySetInnerHTML={{__html: conditions.join("")}}></td>
                <td>{val.subcategory}</td>
            </tr>
        )
    }

    selectCategory(category: string) {
        this.setState({...this.state, examplesCategory: category});
    }

    renderAuthors() {
        return (
            <div className="authors-panel">
                <span className="authors">
                    <span className="title">AUTHORS</span>
                    Sławomir Dadas, Małgorzata Grębowiec, Michał Perełkiewicz, Rafał Poświata<br/>

                </span>
                <span className="affiliation">
                    <span className="title">AFFILIATION</span>
                    National Information Processing Institute
                </span>
                <span className="published">
                    <span className="title">UPDATED</span>
                    {this.state.changelog.length > 0 ? this.state.changelog[0].updated : ""}
                </span>
            </div>
        )
    }

    renderChangelog() {
        const models = [];
        const limit = Math.min(this.state.changelog.length, 5);
        for (let i = 0; i < limit; i++) {
            const key = this.state.changelog[i].model;
            models.push(<span key={key}>{key} ({this.state.changelog[i].updated})</span>);
            models.push(i < (limit - 1) ? ", " : "");
        }
        return <p><strong>Recently added models: </strong>{models}</p>;
    }

    render() {
        if (this.state.data == null) return null;
        const metric: string = this.state.metric || this.state.data.metrics[0].id;
        return (
            <main>
                <h1 key="introduction" id="introduction">
                    {this.state.data.title}
                    {this.renderPaperInfo()}
                </h1>
                {this.renderAuthors()}
                <p key="intro">
                <strong>Large language models (LLMs)</strong> are becoming increasingly proficient in processing and
                    generating multilingual texts, which allows them to address real-world problems more effectively.
                    However, language understanding is a far more complex issue that goes beyond simple text analysis.
                    It requires familiarity with cultural context, including references to everyday life, historical
                    events, traditions, folklore, literature, and pop culture. A lack of such knowledge can lead to
                    misinterpretations and subtle, hard-to-detect errors. To examine language models' knowledge of the
                    Polish cultural context, we introduce the <strong>Polish Linguistic and Cultural Competency
                    Benchmark</strong>, consisting of <strong>600 manually crafted questions</strong>. The benchmark is
                    divided into six categories: history, geography, culture & tradition, art & entertainment, grammar,
                    and vocabulary. This evaluation provides a new perspective on Polish competencies in language
                    models, moving past traditional natural language processing tasks and general knowledge assessment.
                </p>
                {this.renderChangelog()}
                <h2 key="leaderboard" id="leaderboard">Leaderboard</h2>
                {this.renderQuickFilters()}
                {this.renderFilters()}
                <TableView data={this.state.data} mainMetric={metric} tags={this.state.filterSet}
                           modelTypeFilter={this.state.modelTypeFilter}
                           dateRangeFilter={this.state.dateRangeFilter}
                           providerFilter={this.state.providerFilter}
                           sizeLimitFilter={this.state.sizeLimitFilter}/>
                <Tooltip id="table-tooltip" openOnClick={true} variant="light"/>
                <h2 key="examples" id="examples">Examples</h2>
                <p key="examples_intro">
                    The table below presents sample questions from our benchmark. We selected 10 questions from each
                    category, aiming to ensure thematic and structural diversity. Click on the tabs to switch between
                    categories.</p>
                {this.renderExamples()}
                <h2 key="description" id="description">Description</h2>

                <div key="description1" style={{display: "flex", justifyContent: "space-between", gap: "35px"}}>
                    <p>
                        Polish linguistic and cultural competency benchmark comprises of hand-crafted questions designed
                        to evaluate LLM's factual knowledge on Polish culture, tradition and language. The level of
                        difficulty of the questions varies, from those that would be answered by the majority of Poles
                        to detailed questions focusing on region-specific culture or ethnic minorities. The questions
                        have been phrased in such a way that it is deterministically possible to verify their
                        correctness. Approximately half of them are various forms of closed-ended questions such as
                        single-choice, multiple-choice, matching two sets of concepts, or filtering concepts from a
                        list. The rest of the questions usually require answers consisting of a single sentence
                        containing a specific fact or a set of facts. Open-ended questions allow some freedom for the
                        model to generate an answer, but the response should refer to specific entities such as people,
                        dates, numbers, places, certain concepts or phrases. In addition, each question is typically
                        supplemented with instructions for the model, imposing a specific form of answer - short,
                        precise, without additional comments or elaborate explanations. The dataset has been divided
                        into six categories, with 100 questions in each. These categories include:
                    </p>
                    <img src="pie.svg" alt="Categories chart" width="600" height="600"/>
                </div>
                <ul key="categories_list">
                    <li key="cat1"><strong>Culture & tradition</strong> - The category contains questions about beliefs and
                        religion, which are derived from both Christian and folk traditions, as well as Slavic
                        mythology. It also contains questions about pop culture, including characters and events that
                        have had a significant impact on Polish society. Finally, we also included questions about
                        everyday life, covering Polish customs, cuisine, and clothing, among other topics.
                    </li>
                    <li key="cat2"><strong>Art & entertainment</strong> - This category focuses on fields of art such as
                        literature, painting, sculpture, theater, music, dance, or film. The questions cover works and
                        figures related to Polish art. In addition, this category also features questions related to
                        entertainment, including sports, popular music, television, radio, and people associated with
                        show business.
                    </li>
                    <li key="cat3"><strong>Geography</strong> - The category covers Polish geography and is divided into four
                        subcategories. Two of them deal with structures and phenomena of natural (e.g., mountains,
                        rivers, climate) and man-made origin (e.g., cities, tourist attractions, industrial plants,
                        mines). The third subcategory deals with socio-political geography, which includes questions on
                        population, social problems, national borders, and administrative units. The last group is
                        biogeography, which covers the fauna and flora of Poland.
                    </li>
                    <li key="cat4"><strong>History</strong> - The category covers Polish history from the time of Mieszko I to the
                        present day. It is divided into subcategories corresponding to historical periods. It contains
                        questions about important historical events and figures of significance to Polish politics,
                        science, and humanities.
                    </li>
                    <li key="cat5"><strong>Grammar</strong> - The category deals with the rules and principles that govern the
                        structure of sentences in the Polish language, as well as the rules of spelling (orthography).
                        The questions address both theoretical foundations and practical applications of grammar. In
                        addition to orthography, the questions cover such topics as morphology, parts of a sentence,
                        parts of speech, phonetics, word formation, or rhetorical figures.
                    </li>
                    <li key="cat6"><strong>Vocabulary</strong> - The category verifies LLMs' ability to understand the meaning of
                        words, idioms, sayings and proverbs. The questions mainly focus on less frequently used words
                        and phrases. In addition, slang expressions, regionalisms, dialects, colloquial language, and
                        youth language were also included. Apart from modern Polish, several questions also deal with
                        archaisms and old language.
                    </li>
                </ul>

                <h2 key="grading" id="grading">Grading</h2>
                <p key="grading1">
                    Each question in the benchmark defines one or more conditions that must be met for an answer to be
                    accepted. The scores are binary, a model can receive one or zero points for an answer. Partial
                    points are not possible. This means that an answer is considered correct if and only if all the
                    conditions defined in the question are satisfied. If at least one condition is not met, the model
                    gets zero points for that question. The final score in the benchmark is calculated as a percentage
                    of correct answers.
                </p>

                <p key="grading2">
                    The process of verifying a single question starts with sending it to the model and obtaining the
                    answer. We do not use the system prompt in the evaluation, and the question is encoded using a chat
                    template specific to each model as a single message with the user role. In addition, to ensure
                    deterministic responses, the generation temperature parameter is set to 0. In the second step, we
                    normalize the response. Normalization involves removing all characters except letters and numbers,
                    making all letters lowercase, and then lemmatizing the text, that is, reducing all words to their
                    base forms. Such normalization is necessary for languages with rich morphology like Polish, because
                    only then we are able to match different forms of the same word between the model's response and the
                    question's conditions. After normalization, we check each of the conditions defined in the question.
                    The model scores a point only if all conditions have been verified successfully. Our benchmark
                    supports the following condition types:
                </p>

                <ul key="grading3">
                    <li key="grad1"><strong>Include</strong> - Checks whether the words or phrases defined in the condition occurred
                        in the model's response. In the simplest case, we can provide a list of comma-delimited
                        expressions and the condition will be satisfied only if all these expressions are found in the
                        text. In practice, however, there may be more than one way to formulate the correct answer, so
                        it is allowed to define alternative expressions for each item. In such a case, only one of the
                        provided expressions needs to be in the answer. In other words,
                        the <strong>include</strong> condition can verify any logical formula in conjunctive normal form
                        (CNF), in which individual clauses check the occurrence of a word or phrase in the model's
                        response. Moreover, the condition can be parameterized. Instead of the default behavior of
                        matching all defined expressions, we can specify the minimum (<strong>include_min</strong>) and
                        maximum (<strong>include_max</strong>) number of expressions that should be included in the
                        response. In special cases, we can also disable lemmatization (<strong>lemmatize</strong>) if we
                        want to verify the occurrence of a specific, and not just any, form of a word.
                    </li>
                    <li key="grad2"><strong>Exclude</strong> - This is the inverse of the previous condition. It checks that none of
                        the given words or phrases occur in the answer. The condition is verified in a similar way
                        to <strong>include</strong>. We can also use the <strong>lemmatize</strong> parameter to disable
                        lemmatization when matching words.
                    </li>
                    <li key="grad3"><strong>Order</strong> - Checks whether the words or phrases defined in the condition occur in
                        the response in the expected order. Verification of the condition is almost identical
                        to <strong>include</strong>. The difference is that the positions of the first occurrence of the
                        expression, or any of the alternative expressions, are saved. The condition is considered
                        satisfied if and only if the positions are in ascending order. The condition can be used to
                        verify questions involving sorting or matching information.
                        The <strong>lemmatize</strong> parameter can also be applied.
                    </li>
                    <li key="grad4"><strong>Regex</strong> - This is the most complex condition, which checks whether a defined
                        regular expression occurs in the answer. If no additional parameters are specified, the
                        condition is considered to be satisfied if at least one string matches the regular expression in
                        the text. However, it is possible to control the acceptability criteria through parameters. For
                        example, we can define the minimum (<strong>regex_min</strong>) and maximum
                        (<strong>regex_max</strong>) number of occurrences allowed. Furthermore, it is possible to force
                        all occurrences to have the same number of characters (<strong>regex_match_length</strong>).
                        Finally, we can introduce an additional dictionary criterion (<strong>regex_match_word</strong>).
                        If the parameter is set to <strong>true</strong>, each matched string must be a valid word in
                        Polish, which is verified using an external dictionary. Unlike the other
                        conditions, <strong>regex</strong> is always verified on the unnormalized version of the
                        response, so the <strong>lemmatize</strong> parameter is not applicable.
                    </li>
                </ul>
            </main>
        )
    }
}
